import { config } from "@/config";

export type ApiError = Error & { status?: number; body?: string };

const ACCESS_TOKEN_KEY = "cognito_access_token";

function join(base: string, path: string) {
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setAccessToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
}

/**
 * Generic request wrapper with Bearer token injection.
 */
export async function apiRequest<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const url = join(config.apiBaseUrl, path);

  const headers = new Headers(opts.headers || {});
  if (!headers.has("Content-Type") && !(opts.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const accessToken = getAccessToken();
  if (accessToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  let resp: Response;
  try {
    resp = await fetch(url, { ...opts, headers });
  } catch (networkErr) {
    console.error("Network error:", networkErr);
    throw new Error("Unable to connect to the server. Please check your connection.");
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    const err: ApiError = new Error(`HTTP ${resp.status} for ${path}`);
    err.status = resp.status;
    err.body = text;
    console.error(`API Error: ${resp.status} for ${path}`, text);
    throw err;
  }

  const raw = await resp.text();
  if (!raw) return undefined as unknown as T;

  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T;
  }
}
